import { pathOr } from 'ramda'
import URLSearchParams from 'url-search-params'
import { change } from 'redux-form'

import {
    dateFromURLParam,
    roomsDataToStr,
    getActiveAmenities,
    getActivePrices,
    URLDateParam,
    dateEndFromNights,
    nightsFromPeriod,
} from '../../search/utils'
import {
    BUTTON_ON,
    FILTER_DATES,
    FILTER_MORE,
    FILTER_ROOMS,
    FILTER_SORT,
    FILTER_CODE,
    SEARCH_FILTERS_FORM,
    SEARCH_PARAM_AMENITIES,
    SEARCH_PARAM_DATE_END,
    SEARCH_PARAM_DATE_START,
    SEARCH_PARAM_HOTEL_TYPE,
    SEARCH_PARAM_KEYWORD,
    SEARCH_PARAM_PRICE_RANGE_DYNAMIC,
    SEARCH_PARAM_ROOM,
    SEARCH_PARAM_SORT,
    SEARCH_PARAM_CLIENT_CODE,
    SEARCH_PARAM_CONTRACT_NUMBER,
    SEARCH_PARAM_IATA_CODE,
    SEARCH_PARAM_PROMO_CODE,
    SEARCH_PARAM_WRONGPROMO_CODE,
    SEARCH_PARAM_ERROR_PROMO_CODE,
    ADULTS_DEFAULT,
} from '../../search/constants'
import { amenityFieldName } from '../../search/components/Filters/FilterDetailed/constants'
import filterChange from '../../search/actions/filters/filterChange'
import priceChange from '../../search/actions/filters/priceChange'
import amenityChange from '../../search/actions/filters/amenityChange'
import hotelTypeChange from '../../search/actions/filters/hotelTypeChange'
import filterApply from '../../search/actions/filters/filterApply'
import addRoom from './addRoom'

import { businessCodeFilter, tid } from '../drupalSettings'
import setValue from './setValue'
import {generateId} from 'js/helpers/generate';

const datesFilterInit = async (URLParams, dispatch, state) => {
    const { form: { searchFilters: { dates, datesApply } } } = state

    let dateStart
    let dateEnd

    if (URLParams.has(SEARCH_PARAM_DATE_START) && URLParams.has(SEARCH_PARAM_DATE_END)) {
        dateStart = URLParams.get(SEARCH_PARAM_DATE_START)
        dateEnd = dateEndFromNights(dateStart, URLParams.get(SEARCH_PARAM_DATE_END))

        if (URLDateParam(dateStart) >= URLDateParam(dateEnd)) {
            dateStart = ''
            dateEnd = ''
        }
    }

    if (Array.isArray(dates) && dates.length === 2 && datesApply) {
        if (dateStart) {
            URLParams.delete(SEARCH_PARAM_DATE_START)
            URLParams.delete(SEARCH_PARAM_DATE_END)
        }

        URLParams.append(SEARCH_PARAM_DATE_START, URLDateParam(dates[0]))
        URLParams.append(SEARCH_PARAM_DATE_END, nightsFromPeriod(dates))
    } else if (dateStart && dateEnd) {
        await dispatch(filterChange(FILTER_DATES, [dateFromURLParam(dateStart), dateFromURLParam(dateEnd)]))
        dispatch(filterApply(FILTER_DATES, true))
    }
    return URLParams
}

const roomsFilterInit = async (URLParams, dispatch, state) => {
    const { form: { searchFilters: { rooms, roomsApply } } } = state;

    let filledRooms = rooms;

    if (!filledRooms?.length) {
      const roomsParams = URLParams.getAll(SEARCH_PARAM_ROOM);
      // Migrate rooms info from URL to redux
      if (roomsParams?.length) {
        const roomsToState = []
        roomsParams.forEach((room) => {
          const [adults, childrenQuery = '', babies, id] = room.split(',')
          const [children, agesQuery = ''] = childrenQuery.split(':')
          const childrenAmount = parseInt(children)
          const childrenAge = childrenAmount > 0 ? agesQuery.split('-') : []
          const adultsCount = parseInt(adults, 10)
          const guests = {
            adults: adultsCount ? adultsCount : ADULTS_DEFAULT,
            children: childrenAmount,
            babies: parseInt(babies || 0, 10),
            childrenAge: childrenAge,
            visible: true,
            id: id || generateId(),
          }
          roomsToState.push(guests)
        })
        await dispatch(filterChange(FILTER_ROOMS, roomsToState))
        filledRooms = roomsToState;
      } else {
        filledRooms = await dispatch(addRoom())
      }
    }

    await dispatch(filterApply(FILTER_ROOMS, true))
    // Migrate rooms info from redux to URL
    URLParams.delete(SEARCH_PARAM_ROOM)
    const preparedRooms = roomsDataToStr(filledRooms)
    preparedRooms.forEach(room => URLParams.append(SEARCH_PARAM_ROOM, room))

    return URLParams
}

export const codeFilterInit = async (URLParams, dispatch, state) => {
    const codeApply = pathOr(false, ['form', 'searchFilters', 'codeApply'], state)
    // check if user entered codes
    const clientCode = pathOr(false, ['form', 'searchFilters', 'code', 'clientCode'], state)
    const promoCode = pathOr(false, ['form', 'searchFilters', 'code', 'promoCode'], state)
    const errorPromo = pathOr(false, ['form', 'searchFilters', 'code', 'errorPromo'], state)
    const wrongPromoCodeApplied = pathOr(false, [SEARCH_PARAM_WRONGPROMO_CODE], businessCodeFilter)
    const gotError = pathOr(false, ['form', 'searchFilters', 'code', 'error'], state)

    const fieldsArray = [
        SEARCH_PARAM_CLIENT_CODE,
        SEARCH_PARAM_CONTRACT_NUMBER,
        SEARCH_PARAM_IATA_CODE,
        SEARCH_PARAM_PROMO_CODE,
        SEARCH_PARAM_WRONGPROMO_CODE,
        SEARCH_PARAM_ERROR_PROMO_CODE
    ]

    // Init code filter from drupalSettings
    // and dispatch code params into redux-form
    if ((!codeApply && !clientCode && !gotError) || (!codeApply && !promoCode && !gotError)) {
        fieldsArray.forEach((field) => {
            if (field === SEARCH_PARAM_WRONGPROMO_CODE && wrongPromoCodeApplied && !promoCode) {
                // we should show wrong promo code until user enter correct one or 15min passed
                dispatch(change(SEARCH_FILTERS_FORM, SEARCH_PARAM_PROMO_CODE, businessCodeFilter[SEARCH_PARAM_WRONGPROMO_CODE]))
                businessCodeFilter[SEARCH_PARAM_PROMO_CODE] = businessCodeFilter[SEARCH_PARAM_WRONGPROMO_CODE]
            } else if (promoCode && promoCode !== businessCodeFilter[SEARCH_PARAM_PROMO_CODE]) {
                businessCodeFilter[SEARCH_PARAM_PROMO_CODE] = promoCode
            } else if (field === SEARCH_PARAM_ERROR_PROMO_CODE && errorPromo) {
                businessCodeFilter[SEARCH_PARAM_ERROR_PROMO_CODE] = errorPromo
            } else {
                dispatch(change(SEARCH_FILTERS_FORM, field, businessCodeFilter[field]))
            }
        })

        await dispatch(filterChange(FILTER_CODE, businessCodeFilter))
    }

    await dispatch(filterApply(FILTER_CODE, true))
    return URLParams
}

const moreFilterInit = async (URLParams, dispatch, state) => {
    const {
        form: {
            searchFilters: {
                more: { priceRange, amenities, hotelType },
                moreApply,
            },
        },
    } = state;
    let applyByParams = false
    let priceRangeParam
    let amenitiesParam
    let hotelTypeParam

    /**
     * Price range
     */
    if (URLParams.has(SEARCH_PARAM_PRICE_RANGE_DYNAMIC)) {
        priceRangeParam = URLParams.get(SEARCH_PARAM_PRICE_RANGE_DYNAMIC)
    }

    if (priceRange && moreApply) {
        URLParams.delete(SEARCH_PARAM_PRICE_RANGE_DYNAMIC)
        const activePrices = getActivePrices(priceRange)
        if (activePrices.length) {
            URLParams.append(SEARCH_PARAM_PRICE_RANGE_DYNAMIC, activePrices.join(','))
        }
    } else if (priceRangeParam) {
        dispatch(priceChange(priceRangeParam.split(',')))
        applyByParams = true
    }

    /**
     * Amenities
     */
    if (URLParams.has(SEARCH_PARAM_AMENITIES)) {
        amenitiesParam = URLParams.get(SEARCH_PARAM_AMENITIES)
    }

    if (amenities && moreApply) {
        URLParams.delete(SEARCH_PARAM_AMENITIES)
        const activeAmenities = getActiveAmenities(amenities)
        if (activeAmenities.length) {
            URLParams.append(SEARCH_PARAM_AMENITIES, activeAmenities.map(p => p[0]).join(','))
        }
    } else if (amenitiesParam) {
        amenitiesParam.split(',').forEach((value) => {
            dispatch(amenityChange(amenityFieldName(value), BUTTON_ON))
        })
        applyByParams = true
    }

    /**
     * Hotel type
     */
    if (URLParams.has(SEARCH_PARAM_HOTEL_TYPE)) {
        hotelTypeParam = URLParams.get(SEARCH_PARAM_HOTEL_TYPE)
    }

    if (hotelType && moreApply) {
        URLParams.delete(SEARCH_PARAM_HOTEL_TYPE)
        const activeHotelType = getActiveAmenities(hotelType);
        if (activeHotelType.length) {
            URLParams.append(SEARCH_PARAM_HOTEL_TYPE, activeHotelType.map(p => p[0]).join(','))
        }
    } else if (hotelTypeParam) {
        hotelTypeParam.split(',').forEach(
          type => dispatch(hotelTypeChange(type, BUTTON_ON))
        );
        applyByParams = true
    }

    if (applyByParams) {
        dispatch(filterApply(FILTER_MORE, true))
    }

    return URLParams
}

const sortFilterInit = async (URLParams, dispatch, state) => {
    const { form: { searchFilters: { sort } } } = state;

    let sortParam

    if (URLParams.has(SEARCH_PARAM_SORT)) {
        sortParam = parseInt(URLParams.get(SEARCH_PARAM_SORT), 10)
    }

    if (sort) {
        URLParams.delete(SEARCH_PARAM_SORT)
        URLParams.append(SEARCH_PARAM_SORT, sort)
    } else if (sortParam) {
        await dispatch(filterChange(FILTER_SORT, sortParam))
        dispatch(filterApply(FILTER_SORT, true))
    }

    return URLParams
}

const syncFilters = async (dispatch, getState, updateUrl) => {
    const state = getState()
    const {
        main: {
            searchResultsKeyword,
        },
    } = state
    const params = new URLSearchParams(window.location.search)

    if (searchResultsKeyword && !params.has(SEARCH_PARAM_KEYWORD)) {
        params.append(SEARCH_PARAM_KEYWORD, searchResultsKeyword)
    }
    await datesFilterInit(params, dispatch, state)
    await roomsFilterInit(params, dispatch, state)
    await moreFilterInit(params, dispatch, state)
    await codeFilterInit(params, dispatch, state)
    await sortFilterInit(params, dispatch, state)

    const paramsString = Array.from(params.entries()).length > 0 ? `?${params.toString()}` : ''
    if (updateUrl) {
      window.history.replaceState({}, '', paramsString)
    }
    dispatch(setValue('queryString', paramsString))
}

// Redux action for making filters synchronize with URL query and store queryString values
export default (updateUrl = false) => (
    dispatch, getState
) => syncFilters(dispatch, getState, updateUrl)
